import React, { useEffect, useRef, useState } from 'react';
import DialogShell from '../../components/ui/DialogShell';
import Button from '../../components/ui/Button';
import StatusBanner from '../../components/ui/StatusBanner';
import { API_BASE_URL } from '../../config/api';

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;

function formatBytes(n) {
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.ceil(n / 1024)} KB`;
}

export default function EmployeePhotoUploadModal({ open, onClose, profileId, onUploaded, presentation = 'modal' }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setError('');
      setIsUploading(false);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFile = (event) => {
    const picked = event.target.files?.[0];
    setError('');
    if (!picked) {
      setFile(null);
      return;
    }
    if (!ACCEPTED_MIME.includes(picked.type)) {
      setError('Photo must be a JPEG, PNG, or WebP image.');
      setFile(null);
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError(`Photo must be 2 MB or smaller. This file is ${formatBytes(picked.size)}.`);
      setFile(null);
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(picked));
    setFile(picked);
  };

  const handleUpload = async () => {
    if (!file || !profileId) return;
    setIsUploading(true);
    setError('');
    try {
      const body = new FormData();
      body.append('photo', file);
      const response = await fetch(`${API_BASE_URL}/api/employees/profiles/${profileId}/photos`, {
        method: 'POST',
        credentials: 'include',
        body
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.success === false) {
        throw new Error(payload.error || `Upload failed (${response.status}).`);
      }
      if (onUploaded) await onUploaded(payload.data);
      onClose();
    } catch (err) {
      setError(err?.message || 'Unable to upload photo.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <DialogShell
      open={open}
      onClose={isUploading ? () => {} : onClose}
      title="Upload employee photo"
      size="md"
      presentation={presentation}
      busy={isUploading}
      data-testid={presentation === 'drawer' ? 'employee-photo-upload-drawer' : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isUploading}>Cancel</Button>
          <Button variant="primary" onClick={handleUpload} disabled={!file} loading={isUploading} loadingLabel="Uploading…">
            Upload
          </Button>
        </>
      }
    >
      {error ? <StatusBanner kind="error" message={error} /> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_MIME.join(',')}
          onChange={handleFile}
          aria-label="Choose photo"
          data-testid="employee-photo-file-input"
        />
        <p style={{ fontSize: 12, color: '#5b6471', margin: 0 }}>
          Accepted formats: JPEG, PNG, WebP. Maximum 2 MB. The uploaded photo becomes primary if no photo is set.
        </p>
        {file ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Selected employee preview"
                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #e3e6ec' }}
              />
            ) : null}
            <div style={{ fontSize: 13, color: '#1c2330' }}>
              <div>{file.name}</div>
              <div style={{ color: '#5b6471', fontSize: 12 }}>
                {file.type} · {formatBytes(file.size)}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </DialogShell>
  );
}
